import { useEffect, useMemo, useState } from "react";
import { useOutletContext } from "react-router-dom";
import api, { mediaUrl, formatApiError } from "@/lib/api";
import { parseYouTubeId, youtubeEmbedUrl, youtubeThumbnailUrl } from "@/lib/youtube";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Plus, Trash2, Loader2, ImageOff, Film, Image as ImageIcon, Youtube,
  ChevronUp, ChevronDown, ArrowUp, ArrowDown,
} from "lucide-react";
import { toast } from "sonner";

const SUGGESTED_SECTIONS = ["Venue", "Couple", "Activities", "Moments"];

function sectionName(item) {
  return (item.album || "Moments").trim() || "Moments";
}

function groupBySection(items) {
  const map = new Map();
  for (const item of items) {
    const key = sectionName(item);
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(item);
  }
  for (const list of map.values()) {
    list.sort((a, b) => (a.display_order ?? 0) - (b.display_order ?? 0));
  }
  return [...map.entries()].sort((a, b) => {
    const amin = Math.min(...a[1].map((i) => i.display_order ?? 0));
    const bmin = Math.min(...b[1].map((i) => i.display_order ?? 0));
    return amin - bmin || a[0].localeCompare(b[0]);
  });
}

function flattenSections(sections) {
  return sections.flatMap(([, list]) => list);
}

export default function Gallery() {
  const { eventId } = useOutletContext();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [mediaKind, setMediaKind] = useState("image");
  const [album, setAlbum] = useState("Moments");
  const [caption, setCaption] = useState("");
  const [imageData, setImageData] = useState("");
  const [videoFile, setVideoFile] = useState(null);
  const [videoPreview, setVideoPreview] = useState("");
  const [youtubeUrl, setYoutubeUrl] = useState("");
  const [uploadPct, setUploadPct] = useState(0);
  const [saving, setSaving] = useState(false);
  const [reordering, setReordering] = useState(false);
  const [captionDrafts, setCaptionDrafts] = useState({});
  const [savingCaption, setSavingCaption] = useState({});

  const sections = useMemo(() => groupBySection(items), [items]);
  const existingSectionNames = sections.map(([name]) => name);
  const sectionOptions = [...new Set([...SUGGESTED_SECTIONS, ...existingSectionNames])];

  const load = async () => {
    setLoading(true);
    const { data } = await api.get(`/gallery/${eventId}`);
    setItems(data);
    setCaptionDrafts(Object.fromEntries(data.map((i) => [i.id, i.caption || ""])));
    setLoading(false);
  };

  const saveCaption = async (item) => {
    const next = (captionDrafts[item.id] ?? item.caption ?? "").trim();
    const prev = (item.caption || "").trim();
    if (next === prev) return;
    setSavingCaption((s) => ({ ...s, [item.id]: true }));
    try {
      const { data } = await api.patch(`/gallery/${item.id}`, { caption: next });
      setItems((list) => list.map((x) => (x.id === item.id ? data : x)));
      setCaptionDrafts((d) => ({ ...d, [item.id]: data.caption || "" }));
      toast.success("Caption saved");
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Could not save caption");
      setCaptionDrafts((d) => ({ ...d, [item.id]: item.caption || "" }));
    } finally {
      setSavingCaption((s) => ({ ...s, [item.id]: false }));
    }
  };
  useEffect(() => {
    if (eventId) load();
  }, [eventId]);

  useEffect(() => {
    return () => {
      if (videoPreview) URL.revokeObjectURL(videoPreview);
    };
  }, [videoPreview]);

  const resetForm = () => {
    setCaption("");
    setImageData("");
    setVideoFile(null);
    if (videoPreview) URL.revokeObjectURL(videoPreview);
    setVideoPreview("");
    setYoutubeUrl("");
    setUploadPct(0);
    setMediaKind("image");
  };

  const nextOrder = () => {
    if (!items.length) return 0;
    return Math.max(...items.map((i) => Number(i.display_order) || 0)) + 1;
  };

  const persistOrder = async (ordered) => {
    setReordering(true);
    try {
      const { data } = await api.put("/gallery/reorder", {
        event_id: eventId,
        items: ordered.map((item, idx) => ({
          id: item.id,
          display_order: idx,
          album: sectionName(item),
        })),
      });
      setItems(data);
      setCaptionDrafts((prev) => {
        const next = { ...prev };
        for (const item of data) {
          if (next[item.id] === undefined) next[item.id] = item.caption || "";
        }
        return next;
      });
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Could not update order");
      await load();
    } finally {
      setReordering(false);
    }
  };

  const moveSection = async (sectionIndex, direction) => {
    const next = sectionIndex + direction;
    if (next < 0 || next >= sections.length) return;
    const copy = sections.map(([name, list]) => [name, [...list]]);
    const tmp = copy[sectionIndex];
    copy[sectionIndex] = copy[next];
    copy[next] = tmp;
    await persistOrder(flattenSections(copy));
  };

  const moveItem = async (sectionIndex, itemIndex, direction) => {
    const list = [...sections[sectionIndex][1]];
    const next = itemIndex + direction;
    if (next < 0 || next >= list.length) return;
    const tmp = list[itemIndex];
    list[itemIndex] = list[next];
    list[next] = tmp;
    const copy = sections.map(([name, itemsInSection], idx) => (
      idx === sectionIndex ? [name, list] : [name, [...itemsInSection]]
    ));
    await persistOrder(flattenSections(copy));
  };

  const changeItemSection = async (item, newSection) => {
    const target = (newSection || "").trim() || "Moments";
    if (sectionName(item) === target) return;
    const without = items.filter((i) => i.id !== item.id);
    const names = sections.map(([n]) => n);
    if (!names.includes(target)) names.push(target);
    const byName = new Map(groupBySection(without));
    if (!byName.has(target)) byName.set(target, []);
    byName.get(target).push({ ...item, album: target });
    const flat = names.filter((n) => byName.has(n)).flatMap((n) => byName.get(n));
    for (const [n, list] of byName.entries()) {
      if (!names.includes(n)) flat.push(...list);
    }
    await persistOrder(flat);
    toast.success(`Moved to ${target}`);
  };

  const onImageFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setImageData(reader.result);
    reader.readAsDataURL(file);
  };

  const onVideoFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 200 * 1024 * 1024) {
      toast.error("Video must be 200 MB or smaller");
      e.target.value = "";
      return;
    }
    if (videoPreview) URL.revokeObjectURL(videoPreview);
    setVideoFile(file);
    setVideoPreview(URL.createObjectURL(file));
  };

  const save = async () => {
    const section = album.trim() || "Moments";
    const display_order = nextOrder();

    if (mediaKind === "image") {
      if (!imageData) return toast.error("Add an image URL or upload a file");
      setSaving(true);
      try {
        await api.post("/gallery", {
          event_id: eventId,
          album: section,
          caption,
          media_type: "image",
          image_data: imageData,
          display_order,
        });
        toast.success("Image added");
        setOpen(false);
        resetForm();
        load();
      } catch {
        toast.error("Could not save (image may be too large)");
      } finally {
        setSaving(false);
      }
      return;
    }

    if (mediaKind === "youtube") {
      if (!parseYouTubeId(youtubeUrl)) return toast.error("Enter a valid YouTube URL");
      setSaving(true);
      try {
        await api.post("/gallery", {
          event_id: eventId,
          album: section,
          caption,
          media_type: "youtube",
          youtube_url: youtubeUrl.trim(),
          display_order,
        });
        toast.success("YouTube video added");
        setOpen(false);
        resetForm();
        load();
      } catch (err) {
        toast.error(formatApiError(err.response?.data?.detail) || "Could not add YouTube video");
      } finally {
        setSaving(false);
      }
      return;
    }

    if (!videoFile) return toast.error("Choose a video file to upload");
    setSaving(true);
    setUploadPct(0);
    try {
      const fd = new FormData();
      fd.append("file", videoFile);
      fd.append("caption", caption);
      fd.append("album", section);
      fd.append("display_order", String(display_order));
      await api.post(`/gallery/video/${eventId}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (evt) => {
          if (!evt.total) return;
          setUploadPct(Math.round((evt.loaded / evt.total) * 100));
        },
      });
      toast.success("Video uploaded");
      setOpen(false);
      resetForm();
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Video upload failed");
    } finally {
      setSaving(false);
      setUploadPct(0);
    }
  };

  const remove = async (id) => {
    await api.delete(`/gallery/${id}`);
    toast.success("Removed");
    load();
  };

  const kindLabel = (item) => {
    const t = item.media_type || "image";
    if (t === "youtube") return "YouTube";
    if (t === "video") return "Video";
    return "Photo";
  };

  const youtubeId = parseYouTubeId(youtubeUrl);

  return (
    <div data-testid="gallery-page">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Gallery</h1>
          <p className="text-sm text-[#525252]">
            {items.length} items · {sections.length} section{sections.length === 1 ? "" : "s"}
          </p>
          <p className="mt-1 text-xs text-[#737373]">
            Use sections (Venue, Couple, Activities…) for guest tabs. Reorder photos and sections with the arrows.
          </p>
        </div>
        <Button
          onClick={() => { resetForm(); setOpen(true); }}
          data-testid="add-image-button"
          className="bg-[#1A362D] hover:bg-[#12271f]"
        >
          <Plus className="mr-2 h-4 w-4" /> Add Media
        </Button>
      </div>

      {loading ? (
        <div className="flex h-40 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-[#1A362D]" /></div>
      ) : items.length === 0 ? (
        <div className="flex flex-col items-center rounded-md border border-[#E5E5E5] bg-white p-16 text-[#525252]">
          <ImageOff className="mb-3 h-8 w-8" /> No media yet.
        </div>
      ) : (
        <div className={`space-y-8 ${reordering ? "opacity-70 pointer-events-none" : ""}`}>
          {sections.map(([name, list], sectionIndex) => (
            <div key={name} className="rounded-md border border-[#E5E5E5] bg-white p-4" data-testid={`gallery-section-${name}`}>
              <div className="mb-4 flex items-center justify-between gap-3">
                <div>
                  <h2 className="text-sm font-semibold uppercase tracking-wide text-[#525252]">{name}</h2>
                  <p className="text-xs text-[#737373]">{list.length} item{list.length === 1 ? "" : "s"}</p>
                </div>
                <div className="flex items-center gap-1">
                  <Button
                    type="button"
                    size="icon"
                    variant="outline"
                    disabled={sectionIndex === 0}
                    onClick={() => moveSection(sectionIndex, -1)}
                    title="Move section up"
                    data-testid={`section-up-${name}`}
                  >
                    <ArrowUp className="h-4 w-4" />
                  </Button>
                  <Button
                    type="button"
                    size="icon"
                    variant="outline"
                    disabled={sectionIndex === sections.length - 1}
                    onClick={() => moveSection(sectionIndex, 1)}
                    title="Move section down"
                    data-testid={`section-down-${name}`}
                  >
                    <ArrowDown className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
                {list.map((i, itemIndex) => (
                  <div key={i.id} className="group relative overflow-hidden rounded-md border border-[#E5E5E5] bg-[#FAFAFA]" data-testid={`gallery-item-${i.id}`}>
                    {(i.media_type || "image") === "youtube" ? (
                      <img
                        src={i.image_data || youtubeThumbnailUrl(i.youtube_id || i.youtube_url)}
                        alt={i.caption || "YouTube video"}
                        className="h-44 w-full object-cover bg-black"
                      />
                    ) : (i.media_type || "image") === "video" ? (
                      <video
                        src={mediaUrl(i.video_url)}
                        className="h-44 w-full object-cover bg-black"
                        muted
                        playsInline
                        preload="metadata"
                      />
                    ) : (
                      <img src={i.image_data} alt={i.caption} className="h-44 w-full object-cover" />
                    )}
                    <span className="absolute left-2 top-2 rounded-full bg-black/70 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white">
                      {kindLabel(i)}
                    </span>
                    <div className="absolute right-2 top-2 flex flex-col gap-1 opacity-0 transition-opacity group-hover:opacity-100">
                      <button
                        type="button"
                        onClick={() => moveItem(sectionIndex, itemIndex, -1)}
                        disabled={itemIndex === 0}
                        className="rounded-full bg-white/95 p-1.5 shadow disabled:opacity-40"
                        title="Move earlier"
                        data-testid={`item-up-${i.id}`}
                      >
                        <ChevronUp className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => moveItem(sectionIndex, itemIndex, 1)}
                        disabled={itemIndex === list.length - 1}
                        className="rounded-full bg-white/95 p-1.5 shadow disabled:opacity-40"
                        title="Move later"
                        data-testid={`item-down-${i.id}`}
                      >
                        <ChevronDown className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => remove(i.id)}
                        data-testid={`delete-image-${i.id}`}
                        className="rounded-full bg-white/95 p-1.5 shadow"
                        title="Delete"
                      >
                        <Trash2 className="h-4 w-4 text-red-600" />
                      </button>
                    </div>
                    <div className="space-y-2 p-2">
                      <div className="space-y-1">
                        <Label className="text-[10px] uppercase tracking-wide text-[#737373]">Caption</Label>
                        <Input
                          value={captionDrafts[i.id] ?? i.caption ?? ""}
                          onChange={(e) => setCaptionDrafts((d) => ({ ...d, [i.id]: e.target.value }))}
                          onBlur={() => saveCaption(i)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              e.preventDefault();
                              e.currentTarget.blur();
                            }
                          }}
                          placeholder="Add a caption…"
                          className="h-8 text-xs"
                          disabled={!!savingCaption[i.id]}
                          data-testid={`item-caption-${i.id}`}
                        />
                      </div>
                      <Select value={sectionName(i)} onValueChange={(v) => changeItemSection(i, v)}>
                        <SelectTrigger className="h-8 text-xs" data-testid={`item-section-${i.id}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {sectionOptions.map((opt) => (
                            <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) resetForm(); }}>
        <DialogContent data-testid="gallery-dialog">
          <DialogHeader><DialogTitle>Add Media</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-2">
              <button
                type="button"
                onClick={() => setMediaKind("image")}
                data-testid="media-kind-image"
                className={`flex items-center justify-center gap-2 rounded-md border px-3 py-2 text-sm ${mediaKind === "image" ? "border-[#1A362D] bg-[#1A362D] text-white" : "border-[#E5E5E5]"}`}
              >
                <ImageIcon className="h-4 w-4" /> Photo
              </button>
              <button
                type="button"
                onClick={() => setMediaKind("video")}
                data-testid="media-kind-video"
                className={`flex items-center justify-center gap-2 rounded-md border px-3 py-2 text-sm ${mediaKind === "video" ? "border-[#1A362D] bg-[#1A362D] text-white" : "border-[#E5E5E5]"}`}
              >
                <Film className="h-4 w-4" /> File
              </button>
              <button
                type="button"
                onClick={() => setMediaKind("youtube")}
                data-testid="media-kind-youtube"
                className={`flex items-center justify-center gap-2 rounded-md border px-3 py-2 text-sm ${mediaKind === "youtube" ? "border-[#1A362D] bg-[#1A362D] text-white" : "border-[#E5E5E5]"}`}
              >
                <Youtube className="h-4 w-4" /> YouTube
              </button>
            </div>

            {mediaKind === "image" && (
              <>
                <div className="space-y-2">
                  <Label>Image URL</Label>
                  <Input
                    value={imageData.startsWith("data:") ? "" : imageData}
                    onChange={(e) => setImageData(e.target.value)}
                    placeholder="https://..."
                    data-testid="image-url-input"
                  />
                </div>
                <div className="text-center text-xs text-[#525252]">— or —</div>
                <div className="space-y-2">
                  <Label>Upload Photo</Label>
                  <Input type="file" accept="image/*" onChange={onImageFile} data-testid="image-file-input" />
                </div>
                {imageData && <img src={imageData} alt="preview" className="h-40 w-full rounded object-cover" />}
              </>
            )}

            {mediaKind === "video" && (
              <>
                <div className="space-y-2">
                  <Label>Upload Video</Label>
                  <Input
                    type="file"
                    accept="video/mp4,video/webm,video/quicktime,.mp4,.webm,.mov,.m4v"
                    onChange={onVideoFile}
                    data-testid="video-file-input"
                  />
                  <p className="text-xs text-[#737373]">MP4, WebM, or MOV · max 200 MB · stored on the server</p>
                </div>
                {videoPreview && (
                  <video src={videoPreview} controls className="h-48 w-full rounded bg-black object-contain" />
                )}
                {saving && uploadPct > 0 && (
                  <div className="space-y-1">
                    <div className="h-2 overflow-hidden rounded-full bg-[#E5E5E5]">
                      <div className="h-full bg-[#1A362D] transition-all" style={{ width: `${uploadPct}%` }} />
                    </div>
                    <p className="text-xs text-[#737373]">Uploading… {uploadPct}%</p>
                  </div>
                )}
              </>
            )}

            {mediaKind === "youtube" && (
              <>
                <div className="space-y-2">
                  <Label>YouTube URL</Label>
                  <Input
                    value={youtubeUrl}
                    onChange={(e) => setYoutubeUrl(e.target.value)}
                    placeholder="https://www.youtube.com/watch?v=… or https://youtu.be/…"
                    data-testid="youtube-url-input"
                  />
                  <p className="text-xs text-[#737373]">Supports watch, youtu.be, shorts, and embed links</p>
                </div>
                {youtubeId && (
                  <div className="overflow-hidden rounded-md border border-[#E5E5E5] bg-black">
                    <iframe
                      title="YouTube preview"
                      src={youtubeEmbedUrl(youtubeId)}
                      className="aspect-video h-48 w-full"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    />
                  </div>
                )}
              </>
            )}

            <div className="space-y-2">
              <Label>Section</Label>
              <Input
                value={album}
                onChange={(e) => setAlbum(e.target.value)}
                placeholder="e.g. Venue, Couple, Activities"
                list="gallery-section-suggestions"
                data-testid="gallery-album-input"
              />
              <datalist id="gallery-section-suggestions">
                {sectionOptions.map((opt) => (
                  <option key={opt} value={opt} />
                ))}
              </datalist>
              <div className="flex flex-wrap gap-2">
                {SUGGESTED_SECTIONS.map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => setAlbum(s)}
                    className={`rounded-full border px-2.5 py-1 text-xs ${album === s ? "border-[#1A362D] bg-[#1A362D] text-white" : "border-[#E5E5E5] text-[#525252]"}`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
            <div className="space-y-2">
              <Label>Caption</Label>
              <Input value={caption} onChange={(e) => setCaption(e.target.value)} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-[#1A362D] hover:bg-[#12271f]" data-testid="save-image-button">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Add"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
